import { lazy, Suspense, type ReactNode } from 'react';
import type { Locale, TranslationKey } from '../i18n';
const CalibrationTool = lazy(() => import('../tools/calibration-builder/CalibrationTool')
  .then((module) => ({ default: module.CalibrationTool })));
const EffectiveRadiusTool = lazy(() => import('../tools/effective-radius/EffectiveRadiusTool')
  .then((module) => ({ default: module.EffectiveRadiusTool })));
const SpectrumLabTool = lazy(() => import('../tools/spectrum-lab/SpectrumLabTool')
  .then((module) => ({ default: module.SpectrumLabTool })));
const NuclideLibraryStudio = lazy(() => import('../tools/nuclide-library-studio/NuclideLibraryStudio')
  .then((module) => ({ default: module.NuclideLibraryStudio })));
const NuclideDecayTool = lazy(() => import('../tools/nuclide-decay/NuclideDecayTool')
  .then((module) => ({ default: module.NuclideDecayTool })));
const EnergyFitTool = lazy(() => import('../tools/energy-fit/EnergyFitTool')
  .then((module) => ({ default: module.EnergyFitTool })));
const ATerminalTool = lazy(() => import('../tools/aterminal/ATerminalTool')
  .then((module) => ({ default: module.ATerminalTool })));

function LazyTool({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="tool-loading">Loading…</div>}>{children}</Suspense>;
}

export interface ToolDescriptor {
  id: string;
  route: string;
  nameKey: TranslationKey;
  summaryKey: TranslationKey;
  icon: ReactNode;
  render: (locale: Locale) => ReactNode;
}

function RadiusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <line x1="12" y1="12" x2="21" y2="12" strokeDasharray="2 2" />
    </svg>
  );
}


function SpectrumIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3 19.5h18" />
      <path d="M4 17.5l3-.4 1.8-9.8 1.7 8.8 2.7-1 1.7-5.7 1.8 6.4 3.3.9" />
      <path d="M3.5 4.5v15" />
    </svg>
  );
}


function LibraryIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 4.5h6.5a2.5 2.5 0 0 1 2.5 2.5v12a2.5 2.5 0 0 0-2.5-2.5H4z" />
      <path d="M20 4.5h-6.5A2.5 2.5 0 0 0 11 7v12a2.5 2.5 0 0 1 2.5-2.5H20z" />
      <path d="M6.5 8h3M6.5 11h3M14.5 8h3M14.5 11h3" />
    </svg>
  );
}


function DecayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="6" cy="17" r="2.2" />
      <circle cx="12" cy="11.5" r="2.2" />
      <circle cx="18" cy="6" r="2.2" />
      <path d="M7.7 15.5l2.6-2.5M13.7 10l2.6-2.5" />
      <path d="M9.4 13.2l.9-.2-.2.9M15.4 7.7l.9-.2-.2.9" />
      <path d="M4 5.5h5M4 8h3" strokeDasharray="1.5 1.5" />
    </svg>
  );
}

function EnergyFitIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3.5 18.5h17M5 18.5V5.5" />
      <path d="M6.5 15.5l3.2-4.2 3 1.5 4.8-6.2" />
      <circle cx="6.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="9.7" cy="11.3" r="1" fill="currentColor" stroke="none" />
      <circle cx="12.7" cy="12.8" r="1" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="6.6" r="1" fill="currentColor" stroke="none" />
      <path d="M7 14.8c3.8-1.8 6.6-3.3 11-7.5" strokeDasharray="2 1.5" />
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M6.5 8.2l3 2.8-3 2.8M11.5 14h5.5" />
      <path d="M3 7h18" opacity=".55" />
    </svg>
  );
}

function CalibrationIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3.5" y="4" width="17" height="4.5" rx="1.4" />
      <rect x="3.5" y="9.75" width="17" height="4.5" rx="1.4" />
      <rect x="3.5" y="15.5" width="17" height="4.5" rx="1.4" />
      <circle cx="8" cy="6.25" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="17.75" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export const tools: ToolDescriptor[] = [
  {
    id: 'aterminal',
    route: 'aterminal',
    nameKey: 'aterminalName',
    summaryKey: 'aterminalSummary',
    icon: <TerminalIcon />,
    render: (locale) => <LazyTool><ATerminalTool locale={locale} /></LazyTool>,
  },
  {
    id: 'nuclide-decay',
    route: 'nuclide-decay',
    nameKey: 'nuclideDecayName',
    summaryKey: 'nuclideDecaySummary',
    icon: <DecayIcon />,
    render: (locale) => <LazyTool><NuclideDecayTool locale={locale} /></LazyTool>,
  },
  {
    id: 'nuclide-library',
    route: 'nuclide-library',
    nameKey: 'nuclideLibraryName',
    summaryKey: 'nuclideLibrarySummary',
    icon: <LibraryIcon />,
    render: (locale) => <LazyTool><NuclideLibraryStudio locale={locale} /></LazyTool>,
  },
  {
    id: 'spectrum-lab',
    route: 'spectrum-lab',
    nameKey: 'spectrumLabName',
    summaryKey: 'spectrumLabSummary',
    icon: <SpectrumIcon />,
    render: (locale) => <LazyTool><SpectrumLabTool locale={locale} /></LazyTool>,
  },
  {
    id: 'energy-fit',
    route: 'energy-fit',
    nameKey: 'energyFitName',
    summaryKey: 'energyFitSummary',
    icon: <EnergyFitIcon />,
    render: (locale) => <LazyTool><EnergyFitTool locale={locale} /></LazyTool>,
  },
  {
    id: 'effective-radius',
    route: 'effective-radius',
    nameKey: 'ercName',
    summaryKey: 'ercSummary',
    icon: <RadiusIcon />,
    render: (locale) => <LazyTool><EffectiveRadiusTool locale={locale} /></LazyTool>,
  },
  {
    id: 'calibration',
    route: 'calibration',
    nameKey: 'calName',
    summaryKey: 'calSummary',
    icon: <CalibrationIcon />,
    render: (locale) => <LazyTool><CalibrationTool locale={locale} /></LazyTool>,
  },
];
